import React from 'react';
import { TrendingUp, DollarSign, Truck, BarChart3, Wine, Building2, Users } from 'lucide-react';

export default function PromoFinancialView({ summary, orders, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4]?.map((i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-1/2 mb-3" />
            <div className="h-8 bg-gray-200 dark:bg-slate-600 rounded w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  const totalOrders = Number(summary?.total_orders || 0);
  const totalBarrels = Number(summary?.total_barrels || 0);
  const depositCollected = Number(summary?.total_deposit_collected || 0);
  const balanceCollected = Number(summary?.total_balance_collected || 0);
  const providerDue = Number(summary?.total_provider_due || 0);
  const adtReceived = Number(summary?.total_adt_received || 0);
  const futureBalance = Number(summary?.total_future_balance || 0);
  const expectedRevenue = Number(summary?.total_expected_revenue || 0);
  const totalCollected = depositCollected + balanceCollected;

  // Distinguish ADT vs prestataire encaissements from orders
  const adtDirectDeposits = orders?.filter(o =>
    o?.encaissementType === 'adt' || !o?.encaissementType
  )?.reduce((sum, o) => sum + Number(o?.depositAmount || o?.deposit_amount || 0), 0) || 0;

  const partnerDeposits = orders?.filter(o =>
    o?.encaissementType === 'prestataire' || o?.encaissement_type === 'prestataire'
  )?.reduce((sum, o) => sum + Number(o?.depositAmount || o?.deposit_amount || 0), 0) || 0;

  // Fallback: if no encaissementType data, show total deposit as prestataire (default for promo)
  const adtEncaisse = adtDirectDeposits > 0 ? adtDirectDeposits : 0;
  const prestataireEncaisse = partnerDeposits > 0 ? partnerDeposits : depositCollected;
  const duADTParPrestataires = adtReceived;

  const cards = [
    {
      title: 'Acomptes encaissés par ADT',
      value: `${adtEncaisse?.toFixed(0)}€`,
      sub: 'Encaissements directs ADT',
      icon: Building2,
      color: 'text-green-600',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
    },
    {
      title: 'Acomptes encaissés par prestataires',
      value: `${prestataireEncaisse?.toFixed(0)}€`,
      sub: `${totalBarrels} barrique(s) × 50€`,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
    },
    {
      title: 'Dû à ADT par prestataires',
      value: `${duADTParPrestataires?.toFixed(0)}€`,
      sub: `${totalBarrels} barrique(s) × 20€`,
      icon: TrendingUp,
      color: 'text-teal-600',
      bg: 'bg-teal-50 dark:bg-teal-900/20',
      border: 'border-teal-200 dark:border-teal-800',
    },
    {
      title: 'Total dû prestataires (part)',
      value: `${providerDue?.toFixed(0)}€`,
      sub: `${totalBarrels} barrique(s) × 30€`,
      icon: Truck,
      color: 'text-orange-600',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
    },
    {
      title: 'Solde futur à encaisser',
      value: `${futureBalance?.toFixed(0)}€`,
      sub: 'Soldes non encore payés',
      icon: BarChart3,
      color: 'text-purple-600',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800',
    },
    {
      title: 'CA total attendu',
      value: `${expectedRevenue?.toFixed(0)}€`,
      sub: `${totalBarrels} barrique(s) × 160€`,
      icon: DollarSign,
      color: 'text-amber-600',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Main KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards?.map((card) => {
          const CardIcon = card?.icon;
          return (
            <div
              key={card?.title}
              className={`${card?.bg} rounded-xl border ${card?.border} p-5`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{card?.title}</p>
                  <p className={`text-3xl font-bold ${card?.color}`}>{card?.value}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{card?.sub}</p>
                </div>
                <div className={`p-2 rounded-xl ${card?.bg}`}>
                  <CardIcon className={`h-6 w-6 ${card?.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CA Summary */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Wine className="h-5 w-5 text-amber-600" />
          Récapitulatif CA — Promo Été 2026
        </h3>
        <div className="space-y-3">
          {[
            { label: 'Nombre de commandes actives', value: `${totalOrders}`, highlight: false },
            { label: 'Nombre total de barriques', value: `${totalBarrels}`, highlight: false },
            { label: 'CA total attendu (160€ × barriques)', value: `${expectedRevenue?.toFixed(0)}€`, highlight: true },
            { label: 'Acomptes encaissés par ADT', value: `${adtEncaisse?.toFixed(0)}€`, highlight: false, color: 'text-green-600' },
            { label: 'Acomptes encaissés par prestataires', value: `${prestataireEncaisse?.toFixed(0)}€`, highlight: false, color: 'text-blue-600' },
            { label: 'Montant dû à ADT par prestataires', value: `${duADTParPrestataires?.toFixed(0)}€`, highlight: false, color: 'text-teal-600' },
            { label: 'Solde futur à encaisser (clients)', value: `${futureBalance?.toFixed(0)}€`, highlight: false, color: 'text-purple-600' },
          ]?.map((row) => (
            <div
              key={row?.label}
              className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                row?.highlight
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' :'border-b border-gray-100 dark:border-slate-700'
              }`}
            >
              <span className="text-sm text-gray-600 dark:text-gray-400">{row?.label}</span>
              <span className={`font-bold ${row?.highlight ? 'text-amber-600 text-lg' : (row?.color || 'text-gray-900 dark:text-white')}`}>
                {row?.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Example calculation */}
      <div className="bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Exemple de calcul (3 barriques)
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          {[
            { label: 'Acompte client', value: '50€ × 3 = 150€', color: 'text-green-600' },
            { label: 'Prestataire garde', value: '30€ × 3 = 90€', color: 'text-orange-600' },
            { label: 'ADT récupère', value: '20€ × 3 = 60€', color: 'text-teal-600' },
            { label: 'Solde futur', value: '110€ × 3 = 330€', color: 'text-blue-600' },
            { label: 'Total final client', value: '160€ × 3 = 480€', color: 'text-amber-600' },
          ]?.map((item) => (
            <div key={item?.label} className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-100 dark:border-slate-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">{item?.label}</p>
              <p className={`font-bold mt-0.5 ${item?.color}`}>{item?.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
